import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SchemeProvider } from './contexts/SchemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { canOpenUserPage } from './utils/roles';
import { homeFor } from './utils/corridors';
import UpdateGate from './components/UpdateGate';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ApplicationStatus from './pages/ApplicationStatus';
import VerifyCertificate from './pages/VerifyCertificate';
import Activate from './pages/Activate';
import Terms from './pages/Terms';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Transfers from './pages/Transfers';
import TransferDetail from './pages/TransferDetail';
import PartyLookups from './pages/PartyLookups';
import Quotes from './pages/Quotes';
import Certificates from './pages/Certificates';
import GenerateCertificate from './pages/GenerateCertificate';
import CertificateDetail from './pages/CertificateDetail';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import CreateUser from './pages/CreateUser';
import EditUser from './pages/EditUser';
import ChangePassword from './pages/ChangePassword';
import ApplicationLogs from './pages/ApplicationLogs';
import PerformanceTest from './pages/PerformanceTest';
import AuditExport from './pages/AuditExport';
import PayeeRequests from './pages/PayeeRequests';
import FspKeys from './pages/FspKeys';
import FspiopSigningGuide from './pages/FspiopSigningGuide';
import AsyncCallbacksGuide from './pages/AsyncCallbacksGuide';
import IlpGuide from './pages/IlpGuide';
import FspiopFlowSimulator from './pages/FspiopFlowSimulator';
import GettingStarted from './pages/GettingStarted';
import CbsWebhookGuide from './pages/CbsWebhookGuide';
import ErrorReference from './pages/ErrorReference';
import FspiopCertification from './pages/Certification';

// ── ISO 20022 Corridor ────────────────────────────────────────────────────
import Iso20022Dashboard    from './pages/iso20022/Iso20022Dashboard';
import MessageGenerator     from './pages/iso20022/MessageGenerator';
import MessageValidation    from './pages/iso20022/MessageValidation';
import FlowSimulator        from './pages/iso20022/FlowSimulator';
import Conformance          from './pages/iso20022/Conformance';
import SigningGuide         from './pages/iso20022/SigningGuide';
import IsoCertificates      from './pages/iso20022/Certificates';
import IsoCertificateDetail from './pages/iso20022/CertificateDetail';
import IsoGenerateCertificate from './pages/iso20022/GenerateCertificate';
import MessageLedger        from './pages/iso20022/MessageLedger';
import Traces               from './pages/iso20022/Traces';
import Certification        from './pages/iso20022/Certification';
import CertificationScope   from './pages/iso20022/CertificationScope';
import SwitchDeployment     from './pages/iso20022/SwitchDeployment';
import CallbackEndpoints    from './pages/iso20022/CallbackEndpoints';
import Transactions         from './pages/iso20022/Transactions';
import TransactionDetail    from './pages/iso20022/TransactionDetail';
import RtpTransactions      from './pages/iso20022/RtpTransactions';
import RtpTransactionDetail from './pages/iso20022/RtpTransactionDetail';
import RtpStatuses          from './pages/iso20022/RtpStatuses';
import RtpStatusDetail      from './pages/iso20022/RtpStatusDetail';
import AccountRegistrations from './pages/iso20022/AccountRegistrations';
import RegistrationDetail   from './pages/iso20022/RegistrationDetail';
import AccountModifications from './pages/iso20022/AccountModifications';
import ModificationDetail   from './pages/iso20022/ModificationDetail';
import AccountClosures      from './pages/iso20022/AccountClosures';
import ClosureDetail        from './pages/iso20022/ClosureDetail';
import StatusRequests       from './pages/iso20022/StatusRequests';
import StatusRequestDetail  from './pages/iso20022/StatusRequestDetail';
// ISO 8583 card corridor
import CardDashboard        from './pages/iso8583/CardDashboard';
import CardGettingStarted   from './pages/iso8583/CardGettingStarted';
import CardTestCards        from './pages/iso8583/CardTestCards';
import CardCodes            from './pages/iso8583/CardCodes';
import CardMessageGenerator from './pages/iso8583/CardMessageGenerator';
import CardExplainFrame     from './pages/iso8583/CardExplainFrame';
import CardDialect          from './pages/iso8583/CardDialect';
import ClientLibraries      from './pages/iso8583/ClientLibraries';
import CardCertification    from './pages/iso8583/CardCertification';
import CardLedger           from './pages/iso8583/CardLedger';
import XbOverview           from './pages/xb/XbOverview';
import XbProfile            from './pages/xb/XbProfile';
import XbIncoming           from './pages/xb/XbIncoming';
import XbQuotes             from './pages/xb/XbQuotes';
import XbGettingStarted     from './pages/xb/XbGettingStarted';
import XbSamples            from './pages/xb/XbSamples';

// ── Platform Admin (AfricaNenda operators ONLY) ──────────────────────────
// The platform console (tenant CRUD, API-key issuance) is a separate audience
// from DFSP participants. It is EXCLUDED from the participant portal build and
// enabled only for AfricaNenda's separate admin deployment via
// VITE_ENABLE_PLATFORM_ADMIN=true. Lazy-loaded behind the compile-time flag so
// the platform code is tree-shaken out of participant builds entirely.
const ENABLE_PLATFORM_ADMIN = import.meta.env.VITE_ENABLE_PLATFORM_ADMIN === 'true';

const PlatformRoute     = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./components/PlatformRoute')) : null;
const PlatformLayout    = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/PlatformLayout')) : null;
const PlatformLogin     = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/PlatformLogin')) : null;
const PlatformDashboard = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/PlatformDashboard')) : null;
const TenantList        = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/TenantList')) : null;
const TenantDetail      = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/TenantDetail')) : null;

// A user's page opens to that user, or to an admin — as the engine allows. Anyone
// else gets the Access Denied page the admin-only routes show.
function UserDetailRoute() {
  const { id } = useParams();
  const { user } = useAuth();
  if (canOpenUserPage(user, id)) return <UserDetail />;
  return (
    <ProtectedRoute allowedRoles={['ADMIN']}>
      <UserDetail />
    </ProtectedRoute>
  );
}

// The signed-in user's home: the first of their corridors.
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={homeFor(user)} replace />;
}

// One fresh page per tenant. Without the key, going from one tenant to another
// (from the approvals bell, for instance) kept the same component: the previous
// tenant's new API key, reset password, pending Suspend confirmation and open tab
// stayed on screen.
function TenantDetailRoute() {
  const { tenantId } = useParams();
  return <TenantDetail key={tenantId} />;
}
const TenantCreate      = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/TenantCreate')) : null;
const OnboardingQueue   = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/OnboardingQueue')) : null;
const Operators         = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/Operators')) : null;
const PlatformAuthLink  = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/PlatformAuthLink')) : null;
const HubLink           = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/HubLink')) : null;
const HubRunPrint       = ENABLE_PLATFORM_ADMIN ? React.lazy(() => import('./pages/platform/HubRunPrint')) : null;
// Light/dark for the console. Lazy like the rest, so the participant build
// never carries it; each console route provides it, since the login and
// sign-in-link pages sit outside the console layout.
const ConsoleTheme      = ENABLE_PLATFORM_ADMIN
  ? React.lazy(() => import('./pages/platform/useConsoleTheme')
      .then(m => ({ default: m.ConsoleThemeProvider })))
  : null;

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SchemeProvider>
        <UpdateGate />
        {/* Last line of defence for the public pages and the layouts themselves;
            each layout also wraps its page in its own boundary. */}
        <ErrorBoundary>
        <Routes>
          {/* Public */}
          {/* Public landing page (front door) */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          {/* Public self-service onboarding (WP-8) — no auth */}
          <Route path="/register" element={<Register />} />
          <Route path="/register/verify" element={<VerifyEmail />} />
          <Route path="/register/status/:token" element={<ApplicationStatus />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/terms" element={<Terms />} />
          {/* Public certificate check — where a printed certificate's QR code leads */}
          <Route path="/verify/:reportId" element={<VerifyCertificate />} />

          {/* Protected app — pathless layout so children keep their absolute paths
              (/dashboard, /transfers, …); the root "/" is now the public Landing. */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route path="dashboard" element={<Dashboard />} />

            {/* FSPIOP — Transfers */}
            <Route path="transfers" element={<Transfers />} />
            <Route path="transfers/:id" element={<TransferDetail />} />

            {/* FSPIOP — Party Lookups */}
            <Route path="party-lookups" element={<PartyLookups />} />

            {/* FSPIOP — Quotes */}
            <Route path="quotes" element={<Quotes />} />

            {/* FSPIOP - Payee Requests */}
            <Route path="payee-requests" element={<PayeeRequests />} />

            {/* Certificates (JWS key infrastructure) */}
            <Route path="certificates" element={<Certificates />} />
            <Route path="certificates/generate" element={<GenerateCertificate />} />
            <Route path="certificates/:id" element={<CertificateDetail />} />

            {/* Mojaloop Hub Keys */}
            <Route path="fsp-keys" element={<FspKeys />} />

            {/* Build & test — developer guides */}
            <Route path="getting-started" element={<GettingStarted />} />
            <Route path="guides/flow-simulator" element={<FspiopFlowSimulator />} />
            <Route path="guides/signing" element={<FspiopSigningGuide />} />
            <Route path="guides/async-callbacks" element={<AsyncCallbacksGuide />} />
            <Route path="guides/ilp" element={<IlpGuide />} />
            <Route path="guides/cbs-webhook" element={<CbsWebhookGuide />} />
            <Route path="error-codes" element={<ErrorReference />} />

            {/* FSPIOP corridor certification */}
            <Route path="certification" element={<FspiopCertification />} />

            {/* Users — Admin Only */}
            <Route
              path="users"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="users/create"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <CreateUser />
                </ProtectedRoute>
              }
            />
            <Route path="users/:id" element={<UserDetailRoute />} />
            <Route
              path="users/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <EditUser />
                </ProtectedRoute>
              }
            />

            {/* Change Password */}
            <Route path="change-password" element={<ChangePassword />} />

            {/* The Settings page was removed: an old bookmark opens the participant's home, not the sign-in form. */}
            <Route path="settings" element={<HomeRedirect />} />

            {/* Application Logs — Admin Only */}
            <Route
              path="logs"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <ApplicationLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/audit-export"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AuditExport />
                </ProtectedRoute>
              }
            />

            {/* Performance Test — Admin Only, dev mode */}
            <Route
              path="perf"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <PerformanceTest />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* ── ISO 20022 Corridor (/iso20022/*) ─────────────────────── */}
          <Route
            path="/iso20022"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/iso20022/dashboard" replace />} />
            <Route path="dashboard"              element={<Iso20022Dashboard />} />
            <Route path="getting-started"        element={<GettingStarted />} />
            <Route path="generate"               element={<MessageGenerator />} />
            <Route path="validate"               element={<MessageValidation />} />
            <Route path="simulator"              element={<FlowSimulator />} />
            <Route path="signing-guide"          element={<SigningGuide />} />
            <Route path="conformance"            element={<Conformance />} />
            <Route path="certification"          element={<Certification />} />
            <Route path="certification/scope"    element={<CertificationScope />} />
            <Route path="deployment"             element={<SwitchDeployment />} />
            <Route path="error-codes"            element={<ErrorReference />} />
            <Route path="callback-endpoints"     element={<CallbackEndpoints />} />
            <Route path="certificates"           element={<IsoCertificates />} />
            <Route path="certificates/generate"  element={<IsoGenerateCertificate />} />
            <Route path="certificates/:id"       element={<IsoCertificateDetail />} />
            <Route path="ledger"                 element={<MessageLedger />} />
            <Route path="operations/transfers"     element={<MessageLedger operation="transfers" />} />
            <Route path="operations/lookups"       element={<MessageLedger operation="lookups" />} />
            <Route path="operations/registrations" element={<MessageLedger operation="registrations" />} />
            <Route path="operations/traces"        element={<Traces />} />
            <Route path="transactions"           element={<Transactions />} />
            <Route path="transactions/:id"       element={<TransactionDetail />} />
            <Route path="rtp"                    element={<RtpTransactions />} />
            <Route path="rtp/:id"                element={<RtpTransactionDetail />} />
            <Route path="rtp-statuses"           element={<RtpStatuses />} />
            <Route path="rtp-statuses/:id"       element={<RtpStatusDetail />} />
            <Route path="registrations"          element={<AccountRegistrations />} />
            <Route path="registrations/:id"      element={<RegistrationDetail />} />
            <Route path="modifications"          element={<AccountModifications />} />
            <Route path="modifications/:id"      element={<ModificationDetail />} />
            <Route path="closures"               element={<AccountClosures />} />
            <Route path="closures/:id"           element={<ClosureDetail />} />
            <Route path="status-requests"        element={<StatusRequests />} />
            <Route path="status-requests/:id"    element={<StatusRequestDetail />} />
          </Route>

          {/* ── ISO 8583 card corridor (/iso8583/*) ─────────────────── */}
          <Route
            path="/iso8583"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/iso8583/dashboard" replace />} />
            <Route path="dashboard"        element={<CardDashboard />} />
            <Route path="getting-started"  element={<CardGettingStarted />} />
            <Route path="test-cards"       element={<CardTestCards />} />
            <Route path="codes"            element={<CardCodes />} />
            <Route path="message-generator" element={<CardMessageGenerator />} />
            <Route path="explain"          element={<CardExplainFrame />} />
            <Route path="dialect"          element={<CardDialect />} />
            <Route path="client-libraries" element={<ClientLibraries />} />
            <Route path="certification"    element={<CardCertification />} />
            <Route path="ledger"           element={<CardLedger />} />
          </Route>

          {/* ── Cross-border corridor (/xb/*): the participant's regional schemes,
              one at a time, chosen in the sidebar. Held through scheme profiles. ── */}
          <Route
            path="/xb"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/xb/overview" replace />} />
            <Route path="overview"         element={<XbOverview />} />
            <Route path="profile"          element={<XbProfile />} />
            <Route path="incoming"         element={<XbIncoming />} />
            <Route path="quotes"           element={<XbQuotes />} />
            <Route path="getting-started"  element={<XbGettingStarted />} />
            <Route path="samples"          element={<XbSamples />} />
          </Route>

          {/* ── Platform Admin (/platform/*) — AfricaNenda deployment only ──
              Registered only when VITE_ENABLE_PLATFORM_ADMIN=true. In the
              participant build these routes do not exist, so /platform/* falls
              through to the catch-all and redirects to /login. */}
          {ENABLE_PLATFORM_ADMIN && (
            <>
              <Route path="/platform/login" element={
                <Suspense fallback={null}><ConsoleTheme><PlatformLogin /></ConsoleTheme></Suspense>
              } />
              {/* Where an emailed sign-in link lands; public by nature — the
                  person is not signed in until this page redeems it. */}
              <Route path="/platform/auth/link" element={
                <Suspense fallback={null}><ConsoleTheme><PlatformAuthLink /></ConsoleTheme></Suspense>
              } />
              {/* A Hub link check run to print: signed in, but outside the console
                  layout so the page prints on its own. */}
              <Route path="/platform/hub/runs/:runId/print" element={
                <Suspense fallback={null}><ConsoleTheme><PlatformRoute><HubRunPrint /></PlatformRoute></ConsoleTheme></Suspense>
              } />
              <Route
                path="/platform"
                element={
                  <Suspense fallback={null}>
                    <ConsoleTheme>
                      <PlatformRoute>
                        <PlatformLayout />
                      </PlatformRoute>
                    </ConsoleTheme>
                  </Suspense>
                }
              >
                <Route index element={<Navigate to="/platform/dashboard" replace />} />
                <Route path="dashboard" element={<PlatformDashboard />} />
                <Route path="onboarding" element={<OnboardingQueue />} />
                <Route path="operators" element={<Operators />} />
                <Route path="tenants" element={<TenantList />} />
                <Route path="tenants/create" element={<TenantCreate />} />
                <Route path="tenants/:tenantId" element={<TenantDetailRoute />} />
                <Route path="hub" element={<HubLink />} />
              </Route>
            </>
          )}

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </ErrorBoundary>
        </SchemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
